import React, { useEffect } from 'react';

const HomePage = ({ translations, language }) => {
  const t = translations[language];
  const [openDays, setOpenDays] = React.useState([]); // Start with all days collapsed

  const toggleDay = (dayIndex) => {
    if (openDays.includes(dayIndex)) {
      setOpenDays(openDays.filter(i => i !== dayIndex));
    } else {
      setOpenDays([...openDays, dayIndex]);
    }
  };

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-visible');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="page-wrap">
      {/* Hero Section */}
      <div className="hero" id="home">
        <div className="hero-content">
          <div className="hero-pattern"></div>
          <h1 className="hero-title">{t.hero.title}</h1>
          <div className="hero-divider"></div>
          <p className="hero-welcome">{t.hero.welcome}</p>
        </div>
      </div>

      {/* Introduction */}
      <section className="section-intro animate-section" id="intro">
        <div className="container">
          <h2 className="section-title">{t.intro.title}</h2>
          <p className="intro-text">{t.intro.description}</p>
        </div>
      </section>

      {/* Accommodations */}
      <section className="section-accommodations animate-section" id="accommodations">
        <div className="container">
          <h2 className="section-title">{t.accommodations.title}</h2>
          <p className="intro-text">{t.accommodations.description}</p>
          
          <div className="accommodation-grid">
            {t.accommodations.locations.map((location, idx) => (
              <div key={idx} className="accommodation-card">
                <div className="accommodation-image-wrap">
                  <img src={location.image} alt={location.name} className="accommodation-image" />
                </div>
                <div className="accommodation-content">
                  <h3 className="accommodation-name">{location.name}</h3>
                  <p className="accommodation-desc">{location.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Itinerary */}
      <section className="section-itinerary animate-section" id="itinerary">
        <div className="container">
          <h2 className="section-title">{t.itinerary.title}</h2>
          <p className="intro-text">{t.itinerary.subtitle}</p>
          
          <div className="itinerary-timeline">
            {t.itinerary.days.map((day, idx) => {
              const isOpen = openDays.includes(idx);
              
              return (
                <div key={idx} className="itinerary-day">
                  <div 
                    className="day-header-clickable"
                    onClick={() => toggleDay(idx)}
                  >
                    <div className="day-number">
                      <span>{language === 'en' ? 'Day' : 'Jour'} {idx + 1}</span>
                    </div>
                    <div className="day-title-wrapper">
                      <h3 className="day-title">{day.title}</h3>
                      <button className={`expand-btn ${isOpen ? 'open' : ''}`}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className={`day-content ${isOpen ? 'open' : 'closed'}`}>
                    <div className="day-activities">
                      {day.activities.map((activity, i) => (
                        <div key={i} className="activity">
                          <div className="activity-time">{activity.time}</div>
                          <div className="activity-content">
                            <div className="activity-hero">
                              <img src={activity.image} alt={activity.name} className="activity-hero-img" />
                              <div className="activity-hero-overlay">
                                <h4 className="activity-name">{activity.name}</h4>
                              </div>
                            </div>
                            <div className="activity-info">
                              <div className="activity-location">
                                <span className="location-badge">📍 {activity.location}</span>
                              </div>
                              <p className="activity-desc">{activity.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {day.note && <p className="day-note">💡 {day.note}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="section-tips animate-section" id="tips">
        <div className="container">
          <h2 className="section-title">{t.tips.title}</h2>
          <div className="tips-grid">
            {t.tips.items.map((tip, idx) => (
              <div key={idx} className="tip-card">
                <div className="tip-icon">{tip.icon}</div>
                <h3 className="tip-title">{tip.title}</h3>
                <p className="tip-desc">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-contact animate-section" id="contact">
        <div className="container max-w-3xl">
          <h2 className="section-title">{t.contact.title}</h2>
          <div className="contact-card">
            <p className="contact-msg">{t.contact.message}</p>
            
            <div className="contact-methods">
              <a href="mailto:info@fatmaandtarik.com" className="contact-method">
                <div className="method-icon">📧</div>
                <div>
                  <div className="method-label">{t.contact.email}</div>
                  <div className="method-value">info@fatmaandtarik.com</div>
                </div>
              </a>
              
              <div className="contact-method">
                <div className="method-icon">📱</div>
                <div>
                  <div className="method-label">{t.contact.phone}</div>
                  <div className="method-value">Fatma: +32 470 86 68 11</div>
                  <div className="method-value">Tarik: +32 476 70 12 82</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;